import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import api from "~/api2"
import ILongTermReservation from "~/interfaces/ILongTermReservation"

const useLongTermReservation = ({ id, campId }: { id?: string, campId?: string }) => {
  const fetchLongTermReservation = useFetchLongTermReservation(id)
  const updateLongTermReservation = useUpdateLongTermReservation(id)
  const deleteLongTermReservation = useDeleteLongTermReservation(id)
  const createFinalInvoice = useCreateFinalInvoice(campId, id)

  const isLoading = createFinalInvoice.isPending || fetchLongTermReservation.isLoading || updateLongTermReservation.isPending || deleteLongTermReservation.isPending
  const isError = createFinalInvoice.isError || fetchLongTermReservation.isError || updateLongTermReservation.isError || deleteLongTermReservation.isError
  const error = createFinalInvoice.error || updateLongTermReservation.error || deleteLongTermReservation.error

  return {
      resource: fetchLongTermReservation.data,
      update: updateLongTermReservation.mutate,
      destroy: deleteLongTermReservation.mutate,
      createFinalInvoice: createFinalInvoice.mutate,
      isLoading,
      isError,
      error
    }
}

const useFetchLongTermReservation = (id: string) => {
  return useQuery({
    queryKey: ['longTermReservation', id],
    queryFn: async () => {
      const response = await api.LongTermReservations.fetch(id)
      return response.long_term_reservation
    },
    enabled: !!id,
    staleTime: 120000,
    retry: 1
  })
}

const useUpdateLongTermReservation = (id: string) => {
  const queryClient = useQueryClient()
  return useMutation(
    {
      mutationFn: async (params: ILongTermReservation) =>
        (await api.LongTermReservations.update(id, params))?.long_term_reservation,
      onSuccess: (data) => queryClient.setQueryData(['longTermReservation', id], data)
    }
  )
}

const useDeleteLongTermReservation = (id: string) => {
  return useMutation({
    mutationFn: async () =>
      await api.LongTermReservations.destroy(id)
  })
}

const useCreateFinalInvoice = (campId: string, id: string) => {
  return useMutation({
    mutationFn: async () => {
      const response = await api.LongTermReservations.createFinalInvoice(campId, id)
      return response.reservation
    }
  })
}

export default useLongTermReservation
